<template>
  <div class="registros-page">
    <!-- Cabeçalho -->
    <header class="header">
      <h1 class="titulo">📅 Meus Registros de Ponto</h1>
    </header>

    <!-- Filtro -->
    <div class="filtro-box">
      <div class="filtro-linha">
        <input type="date" v-model="dataSelecionada" class="input-date" />
        <button
          @click="buscarRegistros"
          class="btn-principal"
          :disabled="!dataSelecionada || loading"
        >
          <i class="fas fa-search"></i>
          <span>Buscar</span>
        </button>
      </div>

      <div class="filtro-linha">
        <button
          class="btn-secundario"
          @click="abrirModalPDF"
          :disabled="loading"
        >
          <i class="fas fa-file-pdf"></i>
          <span>Exportar PDF</span>
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Carregando registros...</p>
    </div>

    <!-- Nenhum registro -->
    <div v-else-if="registros.length === 0" class="empty-state">
      <i class="fas fa-clock empty-icon"></i>
      <h3>Nenhum registro encontrado</h3>
      <p>Não há registros para a data selecionada.</p>
    </div>

    <!-- Timeline -->
    <div v-else class="timeline">
      <transition-group name="fade" tag="div">
        <div v-for="(registro, index) in registros" :key="registro.id" class="timeline-item">
          <div class="timeline-marker" :class="'tipo-' + registro.tipo"></div>
          <div class="timeline-content">
            <div class="timeline-header">
              <span class="tipo" :class="'tipo-' + registro.tipo">{{ registro.tipo }}</span>
              <span class="data">{{ formatDate(registro.timestamp) }}</span>
            </div>
            <p><strong>Latitude:</strong> {{ registro.latitude.toFixed(5) }}</p>
            <p><strong>Longitude:</strong> {{ registro.longitude.toFixed(5) }}</p>
            <p><strong>Observação:</strong> {{ registro.observacao || '-' }}</p>
          </div>
          <div
            v-if="index < registros.length - 1"
            class="timeline-line"
          ></div>
        </div>
      </transition-group>
    </div>

    <!-- MODAL PDF -->
    <transition name="fade">
      <div v-if="showModalPDF" class="modal-overlay" @click.self="fecharModalPDF">
        <div class="modal">
          <button class="modal-close" @click="fecharModalPDF">✕</button>
          <h3 class="modal-title">Exportar PDF</h3>
          <p class="modal-subtitle">Selecione o período para gerar o relatório.</p>

          <div class="form-row">
            <label>Data início</label>
            <input type="date" v-model="dataInicio" class="input-date w-full" />
          </div>
          <div class="form-row">
            <label>Data fim</label>
            <input type="date" v-model="dataFim" class="input-date w-full" />
          </div>

          <div class="modal-acoes">
            <button class="btn-secundario" @click="fecharModalPDF">Cancelar</button>
            <button class="btn-principal" @click="exportarPDF" :disabled="!dataInicio || !dataFim || loading">
              <i class="fas fa-file-pdf"></i>
              Gerar PDF
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { pontoService } from '@/services/pontoService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export default {
  name: 'RegistrosList',
  data() {
    return {
      loading: false,
      registros: [],
      dataSelecionada: '',
      dataInicio: '',
      dataFim: '',
      showModalPDF: false
    };
  },
  methods: {
    async buscarRegistros() {
      if (!this.dataSelecionada) return;
      this.loading = true;
      this.registros = [];
      try {
        const response = await pontoService.getRegistrosPorData(this.dataSelecionada);
        this.registros = response.data.sort((a, b) => a.id - b.id);
      } catch (error) {
        console.error('Erro ao buscar registros:', error);
        alert(error.response?.data?.detail || 'Erro ao buscar registros');
      } finally {
        this.loading = false;
      }
    },

    abrirModalPDF() {
      this.showModalPDF = true;
      this.dataInicio = '';
      this.dataFim = '';
    },
    fecharModalPDF() {
      this.showModalPDF = false;
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      return new Date(timestamp).toLocaleString('pt-BR');
    },

    segundosParaHora(segundos) {
      if (!segundos && segundos !== 0) return '-';

      const h = Math.floor(segundos / 3600);
      const m = Math.floor((segundos % 3600) / 60);
      const s = segundos % 60;

      return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
    },
    
    async exportarPDF() {
      if (!this.dataInicio || !this.dataFim) {
        alert('Selecione o período para exportar.');
        return;
      }

      try {
        const response = await pontoService.getEspelhoPonto(this.dataInicio, this.dataFim);
        
        const { totais, registros } = response.data;

        const doc = new jsPDF();

        const pageWidth = doc.internal.pageSize.getWidth();

        // 🔹 LOGO (se quiser usar imagem base64 depois)
        // doc.addImage(logoBase64, 'PNG', 14, 10, 25, 12);

        // 🔹 Nome da empresa
        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('PLANNED MÓVEIS PLANEJADOS', 14, 15);

        doc.setFontSize(9);
        doc.setFont(undefined, 'normal');
        doc.text('Espelho de Ponto', 14, 21);

        // Linha divisória
        doc.setDrawColor(200);
        doc.line(14, 24, pageWidth - 14, 24);

        doc.setFontSize(9);
        doc.text(`Período: ${this.dataInicio} até ${this.dataFim}`, 14, 35);

        // 🔹 TABELA
        const tableData = registros.map(dia => [
          this.formatarDiaAjustado(dia.data),
          dia.entrada !== null ? this.segundosParaHora(dia.entrada) : dia.status_dia,
          dia.saida !== null ? this.segundosParaHora(dia.saida) : dia.status_dia,
          dia.tempo_trabalhado !== null ? this.segundosParaHora(dia.tempo_trabalhado) : dia.status_dia,
          dia.tempo_pausa !== null ? this.segundosParaHora(dia.tempo_pausa) : dia.status_dia
        ]);

        autoTable(doc, {
          startY: 40,
          head: [['Data', 'Entrada', 'Saída', 'Trabalhado', 'Pausa']],
          body: tableData,
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 2,
            valign: 'middle'
          },
          headStyles: {
            fillColor: [30, 41, 59], // azul corporativo escuro
            textColor: 255,
            fontStyle: 'bold'
          },
          alternateRowStyles: {
            fillColor: [245, 247, 250] // cinza bem leve
          },
          didParseCell: function (data) {
            if (data.section === 'body') {
              const status = data.row.raw[1];

              if (status === 'FALTA') {
                data.cell.styles.textColor = [220, 38, 38]; // vermelho
                data.cell.styles.fontStyle = 'bold';
              }

              if (status === 'FOLGA') {
                data.cell.styles.textColor = [107, 114, 128]; // cinza
              }
            }
          }
        });

        // 🔹 RESUMO ESTILO ERP (ULTRA COMPACTO)

        const resumoY = doc.lastAutoTable.finalY + 6;

        doc.setDrawColor(0);
        doc.line(14, resumoY - 3, pageWidth - 14, resumoY - 3);

        doc.setFontSize(8);
        doc.setFont(undefined, 'bold');

        // Linha 1
        doc.text(
          `QTD DIAS TRABALHADOS: ${String(totais.dias_trabalhados).padStart(2, '0')}      ` +
          `TOT. HRS TRAB MÊS: ${totais.tempo_trabalhado}      ` +
          `QTD FALTAS: ${String(totais.faltas).padStart(2, '0')}      ` +
          `TOT. TEMPO PAUSA: ${totais.tempo_pausa}`,
          14,
          resumoY
        );

        // Linha inferior
        doc.line(14, resumoY + 9, pageWidth - 14, resumoY + 9);

        // 🔹 Rodapé com paginação
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(150);
          doc.text(
            `Página ${i} de ${pageCount}`,
            pageWidth - 30,
            doc.internal.pageSize.getHeight() - 10
          );
        }

        doc.save(`espelho_ponto_${this.dataInicio}_${this.dataFim}.pdf`);

      } catch (err) {
        console.error('Erro ao exportar PDF:', err);
        alert('Erro ao gerar relatório PDF.');
      }
    },

    async exportarPDF_old() {
      if (!this.dataInicio || !this.dataFim) {
        alert('Selecione o período para exportar.');
        return;
      }
      try {
        const response = await pontoService.getEspelhoPonto(this.dataInicio, this.dataFim);
        const registros = response.data || [];

        const diasPeriodo = this.diasUteisNoPeriodo(this.dataInicio, this.dataFim);
        const mapaRegistros = new Map(registros.map(r => [r.data, r]));

        const doc = new jsPDF();
        doc.setFontSize(14);
        doc.text('Relatório de Pontos', 14, 20);
        doc.setFontSize(10);
        doc.text(`Período: ${this.dataInicio} a ${this.dataFim}`, 14, 28);

        const tableData = diasPeriodo.map(dataDia => {
          const dia = mapaRegistros.get(dataDia);
          const dataFormatada = this.formatarDiaAjustado(dataDia);
          const entradaHora = dia?.entrada ? dia.entrada.split('T')[1].split('.')[0] : '-';
          const saidaHora = dia?.saida ? dia.saida.split('T')[1].split('.')[0] : '-';
          const tempoTrab = dia?.tempo_trabalhado?.split('.')[0] || '-';
          const tempoPausa = dia?.tempo_pausa?.split('.')[0] || '-';
          return [dataFormatada, entradaHora, saidaHora, tempoTrab, tempoPausa];
        });

        autoTable(doc, {
          startY: 34,
          head: [['Data', 'Entrada', 'Saída', 'Trabalhado', 'Pausa']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 10 },
          headStyles: { fillColor: [243, 244, 246], textColor: [30, 41, 59] }
        });

        let totalSegundosTrabalhado = 0;
        let totalSegundosPausa = 0;

        registros.forEach(dia => {
          totalSegundosTrabalhado += this.tempoParaSegundos(dia.tempo_trabalhado);
          totalSegundosPausa += this.tempoParaSegundos(dia.tempo_pausa);
        });

        const totalHorasTrabalhado = Math.floor(totalSegundosTrabalhado / 3600);
        const totalMinTrabalhado = Math.floor((totalSegundosTrabalhado % 3600) / 60);
        const totalHorasPausa = Math.floor(totalSegundosPausa / 3600);
        const totalMinPausa = Math.floor((totalSegundosPausa % 3600) / 60);

        const diasTrabalhados = registros.length;
        const diasComFalta = diasPeriodo.length - diasTrabalhados;

        const resumoStartY = doc.lastAutoTable.finalY + 8;
        autoTable(doc, {
          startY: resumoStartY,
          head: [['Resumo', '']],
          body: [
            // ['Qtd. Dias Trabalhados', `${diasTrabalhados}`],
            // ['Qtd. Dias com Falta', `${diasComFalta}`],
            ['Horas Trabalhadas', `${totalHorasTrabalhado}h ${totalMinTrabalhado}m`],
            ['Tempo em pausa', `${totalHorasPausa}h ${totalMinPausa}m`]
          ],
          theme: 'plain',
          styles: { fontSize: 11, cellPadding: 3 },
          headStyles: { fontStyle: 'bold', fillColor: [243, 244, 246], textColor: [30, 41, 59] },
          didParseCell: (data) => {
            if (data.section === 'body' && data.column.index === 0) {
              data.cell.styles.fontStyle = 'bold';
            }
          }
        });

        doc.save(`meus_pontos_${this.dataInicio}_${this.dataFim}.pdf`);
      } catch (error) {
        console.error('Erro ao exportar PDF:', error);
        alert(error.response?.data?.detail || 'Erro ao gerar relatório.');
      }
      this.fecharModalPDF();
    },

    diasUteisNoPeriodo(inicio, fim) {
      const dias = [];
      const start = this.parseIsoDateOnly(inicio);
      const end = this.parseIsoDateOnly(fim);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const diaSemana = d.getDay();
        if (diaSemana !== 0 && diaSemana !== 6) { // apenas seg-sex
          const ano = d.getFullYear();
          const mes = String(d.getMonth() + 1).padStart(2, '0');
          const dia = String(d.getDate()).padStart(2, '0');
          dias.push(`${ano}-${mes}-${dia}`);
        }
      }
      return dias;
    },

    tempoParaSegundos(tempoStr) {
      if (!tempoStr || typeof tempoStr !== 'string') return 0;
      const [h, m, sRaw] = tempoStr.split(':');
      const sParts = (sRaw || '0').split('.');
      const s = sParts[0];
      return Number(h || 0) * 3600 + Number(m || 0) * 60 + Number(s || 0);
    },

    parseIsoDateOnly(dataStr) {
      if (!dataStr) return new Date();
      const [y, m, d] = dataStr.split('-').map(Number);
      return new Date(y, (m || 1) - 1, d || 1);
    },

    formatarDiaAjustado(dataIso) {
      // if (!dataIso) return '-';
      // const data = new Date(dataIso);
      // const dataCorrigida = new Date(data.getTime() + data.getTimezoneOffset() * 60000);
      // const dia = String(dataCorrigida.getDate()).padStart(2, '0');
      // const mes = String(dataCorrigida.getMonth() + 1).padStart(2, '0');
      // const ano = dataCorrigida.getFullYear();
      // return `${dia}/${mes}/${ano}`;
      if (!dataIso) return '-';
      const base = dataIso.split('T')[0] || dataIso;
      const [ano, mes, dia] = base.split('-');
      if (!ano || !mes || !dia) return dataIso;
      return `${dia}/${mes}/${ano}`;
    }
  }
};
</script>

<style scoped>
.registros-page {
  max-width: 480px;
  margin: 0 auto;
  padding: 1.5rem 1rem 3rem;
  background: #f8fafc;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  display: flex;
  flex-direction: column;
}

@media (min-width: 768px) {
  .registros-page {
    max-width: 700px;
    padding: 2rem 2.5rem;
    border-radius: 16px;
    background: #f9fafb;
  }
}

/* Cabeçalho */
.header {
  text-align: center;
  margin-bottom: 1.5rem;
}
.titulo {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1e293b;
}

/* Filtro */
.filtro-box {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}
.filtro-linha {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  justify-content: center;
}
.input-date {
  flex: 1;
  min-width: 160px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0.6rem;
  font-size: 1rem;
  color: #334155;
}
.btn-principal {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.btn-principal:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(59, 130, 246, 0.4);
}
.btn-principal:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.btn-secundario {
  background: #e2e8f0;
  color: #0f172a;
  border: none;
  border-radius: 10px;
  padding: 0.7rem 1.2rem;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.btn-secundario:hover:not(:disabled) { background: #cbd5e1; }
.btn-secundario:disabled { opacity: 0.6; cursor: not-allowed; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 999;
}
.modal {
  background: #fff;
  padding: 1.5rem;
  border-radius: 12px;
  max-width: 420px;
  width: 100%;
  position: relative;
  box-shadow: 0 10px 24px rgba(0,0,0,0.15);
}
.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  font-size: 1.2rem;
  cursor: pointer;
  color: #64748b;
}
.modal-title {
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 0.4rem;
}
.modal-subtitle {
  color: #475569;
  margin-bottom: 0.8rem;
}
.modal-acoes {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1rem;
}
.form-row {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.8rem;
}
.w-full { width: 100%; }

/* Loading */
.loading-state {
  text-align: center;
  color: #475569;
  padding: 2rem;
}
.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  margin: 0 auto 1rem;
  animation: spin 1s linear infinite;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Empty state */
.empty-state {
  text-align: center;
  color: #64748b;
  padding: 2rem 1rem;
}
.empty-icon {
  font-size: 2.5rem;
  color: #cbd5e1;
  margin-bottom: 0.8rem;
}
.empty-state h3 {
  color: #1e293b;
  font-weight: 600;
  margin-bottom: 0.3rem;
}

/* Timeline */
.timeline {
  position: relative;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}
.timeline-item {
  display: flex;
  align-items: flex-start;
  position: relative;
  padding-left: 2rem;
}
.timeline-marker {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  position: absolute;
  left: 0;
  top: 0.4rem;
  border: 3px solid white;
  box-shadow: 0 0 0 2px #cbd5e1;
}
.timeline-line {
  position: absolute;
  left: 7px;
  top: 1.8rem;
  width: 2px;
  height: calc(100% + 0.5rem);
  background: #cbd5e1;
  z-index: 0;
}

/* Conteúdo */
.timeline-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
  padding: 0.9rem 1.1rem;
  flex: 1;
  position: relative;
}
.timeline-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.4rem;
}
.tipo {
  font-weight: 700;
  text-transform: capitalize;
  padding: 0.2rem 0.6rem;
  border-radius: 8px;
  font-size: 0.85rem;
}
.tipo-entrada {
  background: #dcfce7;
  color: #166534;
}
.tipo-pausa {
  background: #fef9c3;
  color: #854d0e;
}
.tipo-saida {
  background: #fee2e2;
  color: #991b1b;
}
.data {
  font-size: 0.8rem;
  color: #64748b;
}
.timeline-content p {
  margin: 0.25rem 0;
  color: #334155;
  font-size: 0.9rem;
}

/* Transição */
.fade-enter-active, .fade-leave-active {
  transition: all 0.25s ease;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
</style>
